// ASIS client transport. Browser-side encoder/decoder for the audio frame
// protocol, WS handshake, PTT button plumbing, epoch-aware playback owner
// and idempotent cleanup. The browser never imports the Node-side
// `protocol/frame.ts` (Buffer) — it uses the local `frameCodec` (Uint8Array
// + DataView) so the code is safe in worklets too.
import {
  DIR_MIC,
  DIR_SPEAKER,
  PROTOCOL_VERSION,
  SAMPLE_RATE,
} from '../protocol/constants.js';
import { encodeWebFrame, tryDecodeWebFrame, type DecodedFrame } from './frameCodec.js';
import type { ClientMessage, ServerMessage } from '../protocol/messages.js';

const MAX_FRAME_BYTES = 64 * 1024;
const WS_BUFFER_SOFT_LIMIT = 256 * 1024;
const PTT_MIN_PRESS_MS = 100;
const START_READY_DEADLINE_MS = 8000;
const PROGRESS_INTERVAL_MS = 250;
const FLUSH_ACK_TIMEOUT_MS = 400;
const PTT_MIN_SAMPLES = SAMPLE_RATE / 10;

export type SessionState =
  | 'disconnected'
  | 'connecting'
  | 'listening'
  | 'thinking'
  | 'speaking'
  | 'muted'
  | 'interrupted'
  | 'paused'
  | 'error';

export interface ClientHooks {
  onState(state: SessionState): void;
  onTranscript(
    role: 'user' | 'assistant',
    text: string,
    final: boolean,
    id: string,
    interrupted?: boolean,
  ): void;
  onUsage(estimatedUsd: number | undefined, inputTokens?: number, outputTokens?: number): void;
  onMetrics(firstAudioMs: number | undefined): void;
  onError(code: string, message: string): void;
  onSpeakerFrame?(epoch: number, samples: Int16Array): void;
  onPlayedSamples?(epoch: number, playedSamples: number): void;
}

export interface ClientOptions {
  url: string;
  token: string;
  mode: 'vad' | 'ptt';
  audioContext: AudioContext;
  captureWorkletUrl: string;
  playbackWorkletUrl: string;
  hooks: ClientHooks;
  log?: (msg: string, data?: unknown) => void;
  micEnabled?: boolean;
  now?: () => number;
  setTimeoutFn?: (cb: () => void, ms: number) => unknown;
  clearTimeoutFn?: (handle: unknown) => void;
}

type PttPhase = 'idle' | 'pressed' | 'flushing' | 'done' | 'cancelled';

interface PendingInterrupt {
  epoch: number;
  resolve: (info: { playedSamples: number }) => void;
  reject: (err: Error) => void;
  timer: ReturnType<typeof setTimeout> | null;
}

export class AsisClient {
  private ws: WebSocket | null = null;
  private wsState: 'idle' | 'opening' | 'open' | 'closed' = 'idle';
  private ctx: AudioContext | null = null;
  private capture: AudioWorkletNode | null = null;
  private playback: AudioWorkletNode | null = null;
  private micStream: MediaStream | null = null;
  private micSource: MediaStreamAudioSourceNode | null = null;
  private meterAnalyser: AnalyserNode | null = null;
  private meterBuf: Float32Array | null = null;
  private meterRAF = 0;
  private seq = 0;
  private lastImageAt = -Infinity;

  // Epoch state
  private currentEpoch = 0;
  private latestEpoch = 0;
  private pendingClears = new Set<number>();
  private generationEnded = false;
  private playedSamplesOfEpoch = 0;
  private lastReportedPlayed = 0;
  private progressTimer: ReturnType<typeof setInterval> | null = null;
  private pendingInterrupt: PendingInterrupt | null = null;
  private clearedReported = new Set<number>();
  private doneReported = new Set<number>();

  // Lifecycle
  private wantMute = false;
  private mode: 'vad' | 'ptt';
  private modeLockedAtStart = false;
  private sessionReady = false;
  private ready: { resolve: () => void; reject: (e: Error) => void; timer: ReturnType<typeof setTimeout> } | null = null;
  private cleanupInProgress: Promise<void> | null = null;
  private stopRequested = false;
  private startFailed = false;

  // PTT
  private pttPhase: PttPhase = 'idle';
  private pttPressStartedAt = 0;
  private pttFlushToken = 0;
  private pttAccum: Int16Array[] = [];
  private pttSamples = 0;
  private pttStreamEnabled = false;
  private captureFlushing = false;
  private pttFlushTimer: ReturnType<typeof setTimeout> | null = null;
  private pttReadyToCommit: null | (() => void) = null;

  private readonly opts: ClientOptions;
  private readonly now: () => number;
  private readonly setT: (cb: () => void, ms: number) => unknown;
  private readonly clearT: (handle: unknown) => void;
  private closed = false;
  private starting = false;
  private stopping = false;

  constructor(opts: ClientOptions) {
    this.opts = opts;
    this.ctx = opts.audioContext;
    this.mode = opts.mode;
    this.modeLockedAtStart = false;
    this.now = opts.now ?? (() => performance.now());
    this.setT = (opts.setTimeoutFn as typeof this.setT) ?? ((cb, ms) => setTimeout(cb, ms));
    this.clearT = (opts.clearTimeoutFn as typeof this.clearT) ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>));
  }

  // ---------- lifecycle ----------

  async start(): Promise<void> {
    if (this.closed) throw new Error('client already closed');
    if (this.ws || this.starting) throw new Error('client already starting');
    this.starting = true;
    this.modeLockedAtStart = true;
    this.startFailed = false;
    this.stopRequested = false;
    try {
      if (!this.ctx) throw new Error('audio context missing');
      await this.ctx.resume();
      if (this.stopRequested) throw new Error('start aborted');
      this.opts.hooks.onState('connecting');
      await this.ctx.audioWorklet.addModule(this.opts.captureWorkletUrl);
      if (this.stopRequested) throw new Error('start aborted');
      await this.ctx.audioWorklet.addModule(this.opts.playbackWorkletUrl);
      if (this.stopRequested) throw new Error('start aborted');

      this.playback = new AudioWorkletNode(this.ctx, 'asis-playback', {
        numberOfInputs: 0,
        numberOfOutputs: 1,
        outputChannelCount: [1],
      });
      this.playback.connect(this.ctx.destination);
      this.playback.port.onmessage = (e) => this.onPlaybackMsg(e.data);
      this.playback.port.start?.();

      if (this.opts.micEnabled !== false) {
        try {
          this.micStream = await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
              channelCount: 1,
            },
            video: false,
          });
        } catch (err) {
          await this.fullCleanup();
          throw err;
        }
        if (this.stopRequested) {
          // user hit stop while getUserMedia was pending
          this.micStream.getTracks().forEach((track) => track.stop());
          this.micStream = null;
          await this.fullCleanup();
          throw new Error('start aborted');
        }
        this.micSource = this.ctx.createMediaStreamSource(this.micStream);
        this.capture = new AudioWorkletNode(this.ctx, 'asis-capture', {
          numberOfInputs: 1,
          numberOfOutputs: 0,
        });
        this.capture.port.onmessage = (e) => this.onCaptureMsg(e.data);
        this.capture.port.start?.();
        this.micSource.connect(this.capture);
        // capture starts disabled until ready; PTT/VAD enable it
        this.capture.port.postMessage({ type: 'enabled', value: false });

        this.meterAnalyser = this.ctx.createAnalyser();
        this.meterAnalyser.fftSize = 1024;
        this.meterBuf = new Float32Array(this.meterAnalyser.fftSize);
        this.micSource.connect(this.meterAnalyser);
        this.tickMeter();
      }

      await this.openWs();
      // session is now ready; the mode is locked for the rest of the
      // session. The UI must call stop()+start() to switch mode.
      this.modeLockedAtStart = true;
    } catch (err) {
      this.startFailed = true;
      await this.fullCleanup();
      throw err;
    } finally {
      this.starting = false;
    }
  }

  private openWs(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      let settled = false;
      const fail = (e: Error) => {
        if (settled) return;
        settled = true;
        this.clearT(readyTimer);
        this.wsState = 'closed';
        reject(e);
      };
      const ws = new WebSocket(this.opts.url);
      ws.binaryType = 'arraybuffer';
      this.ws = ws;
      this.wsState = 'opening';
      const readyTimer = this.setT(() => {
        if (!settled) fail(new Error('ready_timeout'));
      }, START_READY_DEADLINE_MS) as ReturnType<typeof setTimeout>;
      this.ready = {
        resolve: () => {
          this.clearT(readyTimer);
          if (!settled) { settled = true; resolve(); }
        },
        reject: fail,
        timer: readyTimer,
      };
      ws.onopen = () => {
        this.wsState = 'open';
        this.sendJson({
          type: 'hello',
          protocol: PROTOCOL_VERSION,
          token: this.opts.token,
          ...(!this.opts.token ? { auth: 'browser' as const } : {}),
          client: 'browser',
          mode: this.mode,
          sampleRate: SAMPLE_RATE,
        });
      };
      ws.onerror = () => {
        this.opts.hooks.onError('ws_error', 'conexión con el servidor fallida');
        fail(new Error('ws_error'));
      };
      ws.onclose = (e) => {
        this.wsState = 'closed';
        this.opts.log?.('ws_close', { code: e.code, reason: e.reason });
        if (this.ready) {
          const r = this.ready;
          this.ready = null;
          this.clearT(r.timer);
          r.reject(new Error(`socket closed before ready (${e.code})`));
        }
        if (this.pendingInterrupt) {
          const p = this.pendingInterrupt;
          this.pendingInterrupt = null;
          if (p.timer) this.clearT(p.timer);
          p.reject(new Error('socket closed'));
        }
        this.opts.hooks.onState('disconnected');
        if (!settled) fail(new Error(`socket closed before ready (${e.code})`));
        void this.fullCleanup();
      };
      ws.onmessage = (e) => this.onWsMessage(e);
    });
  }

  async stop(): Promise<void> {
    if (this.stopping) return;
    this.stopping = true;
    this.stopRequested = true;
    try {
      await this.fullCleanup();
    } finally {
      this.stopping = false;
    }
  }

  // Idempotent. Always tears everything down and resets state so a new
  // start() gets a fresh client.
  async fullCleanup(): Promise<void> {
    if (this.cleanupInProgress) return this.cleanupInProgress;
    this.cleanupInProgress = this.doCleanup();
    try {
      await this.cleanupInProgress;
    } finally {
      this.cleanupInProgress = null;
    }
  }

  private async doCleanup(): Promise<void> {
    // PTT
    this.pttPhase = 'idle';
    this.pttAccum = [];
    this.pttReadyToCommit = null;
    this.captureFlushing = false;
    this.pttSamples = 0;
    this.pttStreamEnabled = false;
    if (this.pttFlushTimer) {
      this.clearT(this.pttFlushTimer);
      this.pttFlushTimer = null;
    }
    // epoch owner state
    this.currentEpoch = 0;
    this.latestEpoch = 0;
    this.pendingClears.clear();
    this.sessionReady = false;
    this.playedSamplesOfEpoch = 0;
    this.lastReportedPlayed = 0;
    this.generationEnded = false;
    this.clearedReported.clear();
    this.doneReported.clear();
    this.wantMute = false;
    this.modeLockedAtStart = false;
    if (this.progressTimer) {
      clearInterval(this.progressTimer);
      this.progressTimer = null;
    }
    if (this.pendingInterrupt) {
      const p = this.pendingInterrupt;
      this.pendingInterrupt = null;
      if (p.timer) this.clearT(p.timer);
      p.reject(new Error('client cleaned up'));
    }
    if (this.ready) {
      const r = this.ready;
      this.ready = null;
      this.clearT(r.timer);
      r.reject(new Error('client cleaned up'));
    }

    // close WS
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      ws.onopen = ws.onclose = ws.onerror = ws.onmessage = null;
      try {
        if (ws.readyState === WebSocket.OPEN) ws.close(1000, 'client_stop');
        else if (ws.readyState === WebSocket.CONNECTING) ws.close(4001, 'client_stop');
      } catch {
        /* ignore */
      }
    }
    this.wsState = 'closed';

    // stop mic
    if (this.micStream) {
      try {
        this.micStream.getTracks().forEach((t) => t.stop());
      } catch {
        /* ignore */
      }
      this.micStream = null;
    }

    // cancel RAF
    if (this.meterRAF) {
      cancelAnimationFrame(this.meterRAF);
      this.meterRAF = 0;
    }

    // disconnect nodes
    const safeDisconnect = (n: AudioNode | null) => {
      if (!n) return;
      try {
        n.disconnect();
      } catch {
        /* ignore */
      }
    };
    safeDisconnect(this.capture);
    safeDisconnect(this.micSource);
    safeDisconnect(this.meterAnalyser);
    safeDisconnect(this.playback);
    if (this.capture) { this.capture.port.onmessage = null; this.capture.port.close?.(); }
    if (this.playback) { this.playback.port.onmessage = null; this.playback.port.close?.(); }
    this.capture = null;
    this.micSource = null;
    this.meterAnalyser = null;
    this.meterBuf = null;
    this.playback = null;

    // close audio context so a fresh one is created on restart
    if (this.ctx) {
      try {
        await this.ctx.close();
      } catch {
        /* ignore */
      }
      this.ctx = null;
    }

    this.closed = true; // the UI creates a fresh client and AudioContext
    this.opts.hooks.onState('disconnected');
  }

  // ---------- public API ----------

  setMode(mode: 'vad' | 'ptt'): void {
    if (this.modeLockedAtStart) return; // mode is locked mid-session
    this.mode = mode;
  }

  getMode(): 'vad' | 'ptt' {
    return this.mode;
  }

  setMute(muted: boolean): void {
    this.wantMute = muted;
    if (muted) {
      this.capture?.port.postMessage({ type: 'enabled', value: false });
    } else if (this.mode === 'vad' && this.sessionReady) {
      // Full duplex: provider VAD may interrupt active playback.
      this.capture?.port.postMessage({ type: 'enabled', value: true });
    } else if (this.mode === 'ptt' && this.pttPhase === 'pressed' && this.sessionReady) {
      this.capture?.port.postMessage({ type: 'enabled', value: true });
    }
    if (muted && (this.pttPhase === 'pressed' || this.pttPhase === 'flushing')) this.pttCancel();
    this.sendJson({ type: 'mute', muted });
    this.opts.hooks.onState(muted ? 'muted' : 'listening');
  }

  interrupt(): Promise<{ playedSamples: number }> {
    if (this.currentEpoch === 0) {
      return Promise.resolve({ playedSamples: 0 });
    }
    if (this.pendingInterrupt) return Promise.reject(new Error('interrupt_in_flight'));
    const epoch = this.currentEpoch;
    this.currentEpoch = 0;
    this.generationEnded = false;
    this.pendingClears.add(epoch);
    this.doneReported.add(epoch);
    this.opts.log?.('interrupt', { epoch });
    return new Promise((resolve, reject) => {
      const timer = this.setT(() => {
        if (this.pendingInterrupt && this.pendingInterrupt.epoch === epoch) {
          this.pendingInterrupt = null;
          this.pendingClears.delete(epoch);
          reject(new Error('interrupt_timeout'));
        }
      }, 2000) as ReturnType<typeof setTimeout>;
      this.pendingInterrupt = { epoch, resolve, reject, timer };
      this.playback?.port.postMessage({ type: 'clear', epoch });
    });
  }

  sendText(text: string): void {
    if (!this.sessionReady) return;
    const trimmed = text.trim();
    if (trimmed.length === 0) return;
    this.opts.hooks.onTranscript('user', trimmed, true, `local-${Date.now()}`);
    this.sendJson({ type: 'text', text: trimmed });
  }

  sendImage(data:string):void {
    if(!this.sessionReady||this.closed||this.stopRequested)return;
    if(this.now()-this.lastImageAt<1000)return;
    if(!/^[A-Za-z0-9+/]+={0,2}$/u.test(data)||data.length%4!==0||data.length>349528)return;
    const bytes=data.length*3/4-(data.endsWith('==')?2:data.endsWith('=')?1:0);
    if(bytes>256*1024)return;
    try{const signature=atob(data.slice(0,4));if(signature.charCodeAt(0)!==255||signature.charCodeAt(1)!==216||signature.charCodeAt(2)!==255)return;}catch{return;}
    this.lastImageAt=this.now();this.sendJson({type:'image',mimeType:'image/jpeg',data});
  }

  // ---------- PTT ----------

  pttDown(): void {
    if (this.mode !== 'ptt') return;
    if (!this.sessionReady) return;
    if (this.pttPhase === 'pressed' || this.pttPhase === 'flushing') return;
    if (this.wantMute) return;
    if (!this.capture || this.opts.micEnabled === false) return;
    this.pttPhase = 'pressed';
    this.pttPressStartedAt = this.now();
    this.pttAccum = [];
    this.pttSamples = 0;
    this.pttStreamEnabled = false;
    if (this.currentEpoch !== 0) void this.interrupt().catch((err) => this.opts.hooks.onError('interrupt_failed', (err as Error).message));
    this.capture.port.postMessage({ type: 'reset' });
    this.capture.port.postMessage({ type: 'enabled', value: true });
  }

  pttUp(): void {
    if (this.mode !== 'ptt') return;
    if (this.pttPhase !== 'pressed') return;
    this.pttPhase = 'flushing';
    this.captureFlushing = true;
    const dur = this.now() - this.pttPressStartedAt;
    const token = ++this.pttFlushToken;
    if (this.pttFlushTimer) this.clearT(this.pttFlushTimer);
    this.pttFlushTimer = this.setT(() => this.finalizePtt('timeout'), FLUSH_ACK_TIMEOUT_MS) as ReturnType<typeof setTimeout>;
    this.pttReadyToCommit = () => this.finalizePtt('flushed', dur);
    this.capture?.port.postMessage({ type: 'flush', token });
  }

  pttCancel(): void {
    if (this.pttPhase === 'idle' || this.pttPhase === 'done') return;
    this.pttPhase = 'cancelled';
    this.pttAccum = [];
    this.captureFlushing = false;
    this.pttStreamEnabled = false;
    this.pttSamples = 0;
    this.capture?.port.postMessage({ type: 'reset' });
    this.capture?.port.postMessage({ type: 'enabled', value: false });
    if (this.pttFlushTimer) {
      this.clearT(this.pttFlushTimer);
      this.pttFlushTimer = null;
    }
    this.pttReadyToCommit = null;
    this.sendJson({ type: 'input.clear' });
  }

  private finalizePtt(reason: 'flushed' | 'timeout', dur?: number): void {
    if (this.pttFlushTimer) {
      this.clearT(this.pttFlushTimer);
      this.pttFlushTimer = null;
    }
    if (this.pttPhase === 'cancelled' || this.pttPhase === 'idle' || this.pttPhase === 'done') {
      this.pttReadyToCommit = null;
      return;
    }
    const effectiveDur = typeof dur === 'number' ? dur : this.now() - this.pttPressStartedAt;
    if (reason !== 'flushed') {
      this.pttPhase = 'done';
      this.pttReadyToCommit = null;
      this.captureFlushing = false;
      this.capture?.port.postMessage({ type: 'reset' });
      this.capture?.port.postMessage({ type: 'enabled', value: false });
      this.pttAccum = [];
      this.sendJson({ type: 'input.clear' });
      this.opts.hooks.onError('capture_flush_timeout', 'captura incompleta; audio descartado');
      return;
    }
    this.captureFlushing = false;
    this.capture?.port.postMessage({ type: 'enabled', value: false });
    if (effectiveDur < PTT_MIN_PRESS_MS || this.pttSamples < PTT_MIN_SAMPLES) {
      this.pttPhase = 'done';
      this.pttReadyToCommit = null;
      this.pttSamples = 0;
      this.capture?.port.postMessage({ type: 'reset' });
      this.pttAccum = [];
      this.sendJson({ type: 'input.clear' });
      return;
    }
    this.commitInput();
    this.pttPhase = 'done';
    this.pttReadyToCommit = null;
  }

  commitInput(): void {
    if (!this.sessionReady || this.mode !== 'ptt') return;
    this.sendJson({ type: 'input.commit' });
  }

  // ---------- WS ----------

  private sendJson(msg: ClientMessage | {type:'image';mimeType:'image/jpeg';data:string}): void {
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    if (ws.bufferedAmount > WS_BUFFER_SOFT_LIMIT) {
      this.opts.hooks.onError('backpressure', 'WS buffer overflow');
      try {
        ws.close(1011, 'backpressure');
      } catch {
        /* ignore */
      }
      return;
    }
    try {
      ws.send(JSON.stringify(msg));
    } catch (err) {
      this.opts.hooks.onError('ws_send', this.sanitizeErr((err as Error).message));
    }
  }

  private sendBinary(bytes: Uint8Array): void {
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    if (ws.bufferedAmount > WS_BUFFER_SOFT_LIMIT) {
      this.opts.hooks.onError('backpressure', 'WS buffer overflow');
      try {
        ws.close(1011, 'backpressure');
      } catch {
        /* ignore */
      }
      return;
    }
    try {
      ws.send(bytes);
    } catch (err) {
      this.opts.hooks.onError('ws_send', this.sanitizeErr((err as Error).message));
    }
  }

  private onWsMessage(ev: MessageEvent): void {
    if (ev.data instanceof ArrayBuffer) {
      this.onBinary(new Uint8Array(ev.data));
      return;
    }
    if (typeof ev.data === 'string') {
      if (ev.data.length > MAX_FRAME_BYTES) return;
      let parsed: ServerMessage;
      try {
        parsed = JSON.parse(ev.data) as ServerMessage;
      } catch {
        return;
      }
      if (parsed && typeof parsed === 'object' && typeof parsed.type === 'string') this.dispatch(parsed);
    }
  }

  private onBinary(buf: Uint8Array): void {
    let frame: DecodedFrame | null;
    try {
      frame = tryDecodeWebFrame(buf, MAX_FRAME_BYTES);
    } catch (err) {
      this.opts.hooks.onError('bad_frame', this.sanitizeErr((err as Error).message));
      return;
    }
    if (!frame) return;
    if (frame.direction !== DIR_SPEAKER) return;
    // stale epoch: drop without disturbing currentEpoch
    if (!this.sessionReady || !this.currentEpoch || frame.epoch !== this.currentEpoch) return;
    this.opts.hooks.onSpeakerFrame?.(frame.epoch, frame.samples);
    // Int16 copy: the worklet consumes an Int16Array, not a Uint8Array.
    const samples = new Int16Array(frame.samples.length);
    samples.set(frame.samples);
    this.playback?.port.postMessage(
      { type: 'pcm', epoch: frame.epoch, samples },
      [samples.buffer],
    );
  }

  private dispatch(msg: ServerMessage): void {
    switch (msg.type) {
      case 'ready': {
        if (!this.ready || this.stopRequested) return;
        this.sessionReady = true;
        this.opts.hooks.onState('listening');
        this.opts.log?.('ready', msg);
        const r = this.ready;
        this.ready = null;
        if (r) r.resolve();
        // in VAD mode, start capturing only now that the server is ready
        if (this.mode === 'vad' && this.opts.micEnabled !== false && !this.wantMute) {
          this.capture?.port.postMessage({ type: 'enabled', value: true });
        }
        return;
      }
      case 'state':
        this.opts.hooks.onState(msg.state === 'idle' ? 'listening' : msg.state);
        return;
      case 'playback.start': {
        if (!Number.isSafeInteger(msg.epoch) || msg.epoch <= this.latestEpoch) return;
        this.latestEpoch = msg.epoch;
        this.currentEpoch = msg.epoch;
        this.playedSamplesOfEpoch = 0;
        this.lastReportedPlayed = 0;
        this.generationEnded = false;
        this.clearedReported.delete(msg.epoch);
        this.doneReported.delete(msg.epoch);
        this.playback?.port.postMessage({ type: 'start', epoch: msg.epoch });
        this.opts.hooks.onState('speaking');
        this.startProgressTimer();
        return;
      }
      case 'playback.end': {
        if (msg.epoch !== this.currentEpoch) return;
        this.generationEnded = true;
        this.playback?.port.postMessage({ type: 'end', epoch: msg.epoch });
        return;
      }
      case 'playback.clear': {
        const epoch = msg.epoch;
        if (epoch !== this.currentEpoch) return;
        this.pendingClears.add(epoch);
        this.doneReported.add(epoch);
        this.currentEpoch = 0;
        this.generationEnded = false;
        this.playback?.port.postMessage({ type: 'clear', epoch });
        this.opts.hooks.onState('listening');
        return;
      }
      case 'transcript':
        this.opts.hooks.onTranscript(
          msg.role,
          msg.text,
          msg.final,
          msg.id ?? `${msg.role}-${this.latestEpoch}`,
          msg.interrupted === true,
        );
        return;
      case 'metrics':
        this.opts.hooks.onMetrics(msg.firstAudioMs);
        return;
      case 'usage':
        this.opts.hooks.onUsage(msg.estimatedUsd, msg.inputTokens, msg.outputTokens);
        return;
      case 'error': {
        this.opts.hooks.onError(msg.code, this.sanitizeErr(msg.message));
        this.ready?.reject(new Error(msg.code));
        return;
      }
      case 'pong':
        return;
      case 'session.end': {
        this.opts.hooks.onState('disconnected');
        void this.fullCleanup();
        return;
      }
    }
  }

  private sanitizeErr(msg: string): string {
    return msg
      .replace(/sk-[A-Za-z0-9_-]+/g, '[redacted]')
      .replace(/Bearer\s+[A-Za-z0-9._-]+/g, 'Bearer [redacted]')
      .replace(/token=[A-Za-z0-9._-]+/g, 'token=[redacted]');
  }

  // ---------- progress reporting ----------

  private startProgressTimer(): void {
    if (this.progressTimer) clearInterval(this.progressTimer);
    this.progressTimer = setInterval(() => {
      if (this.currentEpoch === 0) return;
      const played = this.playedSamplesOfEpoch;
      if (played === this.lastReportedPlayed) return;
      this.lastReportedPlayed = played;
      this.sendJson({
        type: 'playback.progress',
        epoch: this.currentEpoch,
        playedSamples: played,
      });
    }, PROGRESS_INTERVAL_MS);
  }

  // ---------- worklet message handlers ----------

  private onPlaybackMsg(data: unknown): void {
    if (!data || typeof data !== 'object') return;
    const msg = data as {
      type: string;
      epoch?: number;
      playedSamples?: number;
      samples?: number;
      requested?: number;
      available?: number;
    };
    switch (msg.type) {
      case 'played': {
        if (typeof msg.epoch !== 'number' || typeof msg.samples !== 'number') return;
        if (msg.epoch !== this.currentEpoch) return; // stale
        this.playedSamplesOfEpoch = msg.playedSamples ?? this.playedSamplesOfEpoch + msg.samples;
        this.opts.hooks.onPlayedSamples?.(this.currentEpoch, this.playedSamplesOfEpoch);
        // done is decided by the `drained` event only — see below.
        return;
      }
      case 'drained': {
        if (typeof msg.epoch !== 'number') return;
        if (msg.epoch !== this.currentEpoch) return;
        if (typeof msg.playedSamples === 'number') this.playedSamplesOfEpoch = msg.playedSamples;
        if (this.doneReported.has(msg.epoch)) return;
        if (!this.generationEnded) return; // only signal done after end
        this.doneReported.add(msg.epoch);
        this.sendJson({
          type: 'playback.done',
          epoch: this.currentEpoch,
          playedSamples: this.playedSamplesOfEpoch,
        });
        this.currentEpoch = 0;
        return;
      }
      case 'overflow': {
        this.opts.hooks.onError('playback_overflow', `ring overflow e=${msg.epoch}`);
        // fatal: close session
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          try {
            this.ws.close(1011, 'playback_overflow');
          } catch {
            /* ignore */
          }
        }
        void this.fullCleanup();
        return;
      }
      case 'flushed': {
        if (typeof msg.epoch !== 'number' || !this.pendingClears.delete(msg.epoch)) return;
        if (typeof msg.playedSamples !== 'number' || !Number.isSafeInteger(msg.playedSamples) || msg.playedSamples < 0) return;
        const playedSamples = msg.playedSamples;
        if (this.pendingInterrupt && this.pendingInterrupt.epoch === msg.epoch) {
          const pending = this.pendingInterrupt;
          this.pendingInterrupt = null;
          if (pending.timer) this.clearT(pending.timer);
          this.sendJson({ type: 'interrupt', epoch: msg.epoch, playedSamples });
          pending.resolve({ playedSamples });
        }
        if (!this.clearedReported.has(msg.epoch)) {
          this.clearedReported.add(msg.epoch);
          this.sendJson({ type: 'playback.cleared', epoch: msg.epoch, playedSamples });
        }
        return;
      }
    }
  }

  private onCaptureMsg(data: unknown): void {
    if (!data || typeof data !== 'object') return;
    const msg = data as { type: string; samples?: Int16Array; tail?: boolean; token?: number };
    if (msg.type === 'flushed') {
      if (this.pttPhase !== 'flushing' || msg.token !== this.pttFlushToken) return;
      const cb = this.pttReadyToCommit;
      if (cb) {
        this.pttReadyToCommit = null;
        cb();
      }
      return;
    }
    if (msg.type !== 'frame' || !msg.samples) return;
    if (this.wantMute || !this.sessionReady) return;
    const pttTail = this.mode === 'ptt' && this.pttPhase === 'flushing' && this.captureFlushing;
    if (this.mode === 'ptt' && this.pttPhase !== 'pressed' && !pttTail) return;
    const samples = msg.samples;
    if (this.mode === 'ptt') {
      this.pttSamples += samples.length;
      if (!this.pttStreamEnabled) {
        this.pttAccum.push(samples);
        if (this.pttSamples < PTT_MIN_SAMPLES) return;
        this.pttStreamEnabled = true;
        for (const buffered of this.pttAccum) this.sendMicSamples(buffered);
        this.pttAccum = [];
        return;
      }
    }
    this.sendMicSamples(samples);
  }

  private sendMicSamples(samples: Int16Array): void {
    const frame = encodeWebFrame({
      direction: DIR_MIC,
      epoch: 0,
      seq: this.seq++ >>> 0,
      samples,
    });
    this.sendBinary(frame);
  }

  // ---------- meter ----------

  private tickMeter = (): void => {
    if (!this.ctx) return;
    this.meterRAF = requestAnimationFrame(this.tickMeter);
    if (!this.meterAnalyser || !this.meterBuf) return;
    this.meterAnalyser.getFloatTimeDomainData(this.meterBuf);
    let sum = 0;
    for (let i = 0; i < this.meterBuf.length; i++) {
      const v = this.meterBuf[i] ?? 0;
      sum += v * v;
    }
    const rms = Math.sqrt(sum / this.meterBuf.length);
    const db = 20 * Math.log10(rms || 1e-6);
    const root = document.documentElement;
    root.style.setProperty('--rms', String(Math.max(0, Math.min(1, (db + 60) / 60))));
  };

  // ---------- test hooks ----------

  /** @internal expose for tests only */
  _testInternals(): {
    currentEpoch: number;
    generationEnded: boolean;
    playedSamplesOfEpoch: number;
    wsState: string;
    pttPhase: PttPhase;
    modeLockedAtStart: boolean;
  } {
    return {
      currentEpoch: this.currentEpoch,
      generationEnded: this.generationEnded,
      playedSamplesOfEpoch: this.playedSamplesOfEpoch,
      wsState: this.wsState,
      pttPhase: this.pttPhase,
      modeLockedAtStart: this.modeLockedAtStart,
    };
  }
}
